import { Trans, useTranslation } from 'react-i18next';
import { useAppSelector } from '../../common/hooks';
import { RetirementGoalsFormType, GoalTypeProps, CallRunpipePropsType } from '../../common/types/clients-entity';
import dayjs, { Dayjs } from 'dayjs';
import {
    RETIREMENT_GOAL,
    DEFAULT_RETIREMENT_AGE,
    DEFAULT_PLANNING_AGE,
    DEFAULT_CURRENCY_SYMBOL
} from '../../common/constants';
import { ConfigurableDrawer, FormField } from '../../components/organisms/GoalDrawer';
import { ComboSelect, TextboxInput, Typography } from '../../components/atoms';
import { RadioWithOptionalDatePicker } from '../../components/molecules';
import {
    removeNonNumeric,
    formatCurrency,
    priorityOptionsFlow2,
    getGoalPriorityEnum,
    getPriorityLabel,
    isAspirePriorityTenant,
    isMonthYearDatesEnabled
} from '../../utils';
import { addAgeToDate } from '../../common/helper';
import { GoalPriorityInfoText } from '../../components/molecules/GoalPriorityInfoText/GoalPriorityInfoText';
import { Box, useTheme } from '@mui/material';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-blue.svg';
import { useState, useEffect } from 'react';

export interface RetirementGoalProps {
    // eslint-disable-next-line no-unused-vars
    handleClose: (flag: boolean) => void;
    onSubmit?: () => void;
    loading?: boolean;
    heading?: string;
    subHeading?: string;
    selectedGoal?: boolean | GoalTypeProps;
    goalData?: RetirementGoalsFormType;
    yearsInRetirement?: number;
    open: boolean;
    currency?: string;
    callRunpipe?: CallRunpipePropsType;
}

export const RetirementGoals = ({ handleClose, selectedGoal, open, goalData }: RetirementGoalProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const {
        goeConfig,
        globalConfig: { currencySymbol }
    } = useAppSelector((state) => state.global);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const [startDateKey, setStartDateKey] = useState<string>('');
    const [endDateKey, setEndDateKey] = useState<string>('');

    useEffect(() => {
        if (goalData) {
            setStartDateKey(goalData.planStartRetirementKey || '');
            setEndDateKey(goalData.targetDateKey || '');
        }
    }, [goalData]);

    const goalPriorityValue = goeConfig?.['goalPriorityProbabilities'];
    const goalPriorityValueInPercentage =
        goalPriorityValue &&
        Object.fromEntries(
            Object.entries(goalPriorityValue || {}).map(([key, value]) => [key, (value as number) * 100])
        );
    const initialValues: RetirementGoalsFormType = {
        frequency: undefined,
        goalId: '',
        goalName: '',
        goalPriority: undefined,
        goalType: 'Retirement Goal',
        planStartRetirement: null as Dayjs | null,
        planStartRetirementKey: '',
        targetDate: null as Dayjs | null,
        targetDateKey: '',
        targetedRetirementIncome: undefined, //goalAmount to be sent to runpipe
        yearsInRetirement: undefined,
        goalAmount: 0 //legacyAmount
    };

    const additionalFields = () => {
        return {
            planStartRetirementKey: startDateKey,
            targetDateKey: endDateKey
        };
    };

    const goalPriorityEnum = getGoalPriorityEnum(goalPriorityValue);
    const priorityOptions = priorityOptionsFlow2(t, goalPriorityEnum);
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const getPriorityDisplayLabel = (label: string, value: string) => getPriorityLabel({ label, value }, tenant, t);

    const fields: FormField<RetirementGoalsFormType>[] = [
        {
            name: 'goalName',
            label: t('TEXT_GOAL_NAME'),
            type: 'text',
            placeholder: t('TEXT_GOAL_NAME'),
            required: true,
            validation: {
                pattern: /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i
            }
        },
        {
            name: 'goalPriority',
            label: t('TEXT_GOAL_PRIORITY'),
            type: 'select',
            required: true,
            customRender: ({ field, error, onChange }) => {
                return (
                    <ComboSelect
                        onSelectChange={(val) => {
                            onChange && onChange(val);
                        }}
                        onBlur={field.onBlur}
                        selectedValue={(field.value as string) || ''}
                        name={`retirement-goal-priority-${field.name}`}
                        placeholder={t('PRIORITY_TEXT_SELECT') + ` (${priorityOptions.length})`}
                        options={priorityOptions}
                        error={!!error}
                        listItemStyle={{
                            padding: '0px 14px'
                        }}
                        renderMenuItem={(option, index = 0) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                {`${index + 1} - ${getPriorityDisplayLabel(option.label, option.value)}`}
                            </Box>
                        )}
                        renderValue={(selected) => {
                            const selectedOption = priorityOptions.find((option) => option.value === selected);
                            if (selectedOption) {
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {`${selectedOption.index + 1} - ${getPriorityDisplayLabel(
                                            selectedOption.label,
                                            selectedOption.value
                                        )}`}
                                    </Box>
                                );
                            }
                            return (
                                <em style={{ color: theme.palette.info[300] }}>
                                    {t('PRIORITY_TEXT_SELECT') + ` (${priorityOptions.length})`}
                                </em>
                            );
                        }}
                    />
                );
            },
            infoTooltip: <GoalPriorityInfoText values={goalPriorityValueInPercentage} renameWishToAspire={isAspirePriorityTenant(tenant)} />
        },
        {
            name: 'targetedRetirementIncome',
            label: t('INCOME_GOAL_AMOUNT'),
            type: 'currency',
            colspan: 1,
            required: true,
            validation: {
                validate: (value) => {
                    if (!value) {
                        return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    }
                    if (value < 0) {
                        return t('TEXT_AMOUNT_VALIDATION');
                    }
                    return true;
                }
            },
            customRender: ({ field, error, onChange }) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                        <TextboxInput
                            onBlur={field.onBlur}
                            onChange={(e) => {
                                const numericValue = Number(removeNonNumeric(e.target.value));
                                onChange?.(isNaN(numericValue) ? 0 : numericValue);
                            }}
                            value={field.value ? formatCurrency(Number(field.value), true) : ''}
                            inputRef={field.ref}
                            size="small"
                            error={!!error}
                            type="text"
                            data-testid="retirement-goal-income"
                            placeholder={currencySymbol || DEFAULT_CURRENCY_SYMBOL}
                            iconPlacement="start"
                            fullWidth
                        />
                    </Box>
                    <Box mt="4px" display="flex" flexDirection="row" alignItems="center" gap="4px">
                        <InfoIcon />
                        <Typography
                            variant="labels.small"
                            sx={{ color: theme.palette.info[300], textDecoration: 'underline' }}
                            label={t('TEXT_GOAL_TARGET_AMOUNT_HELPER_TEXT')}
                        />
                    </Box>
                </Box>
            ),
            infoTooltip: (
                <Trans i18nKey="INCOME_GOAL_AMOUNT_INFO_TEXT" components={{ strong: <strong />, br: <br /> }} />
            )
        },
        {
            name: 'frequency',
            label: t('FREQUENCY'),
            type: 'select',
            required: true,
            placeholder: t('FREQUENCY_TEXT_SELECT'),
            colspan: 1,
            options: [
                { label: t('ANNUALLY'), value: 'Annually' },
                { label: t('MONTHLY'), value: 'Monthly' }
            ]
        },
        {
            name: 'planStartRetirement',
            label: t('STARTING_AT_LABEL'),
            type: 'date',
            colspan: 1,
            required: true,
            validation: {
                validate: (value) => {
                    if (!value) {
                        return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    }
                    if (dayjs(value, 'DD-MM-YYYY').isBefore(dayjs(), isMonthYearDatesEnabled() ? 'month' : undefined)) {
                        return t('TEXT_VALID_DATE_ERROR');
                    }
                    return true;
                }
            },
            customRender: ({ field, error, onChange, watch }) => (
                <RadioWithOptionalDatePicker
                    showCustomDateField={false}
                    hasError={!!error && error.type !== 'required'}
                    field={{
                        ...field,
                        onChange: (arg: string) => {
                            field.onChange(arg);
                            onChange?.(arg);
                        }
                    }}
                    selectedKey={startDateKey || ''}
                    onSelectedKeyChange={(value) => {
                        setStartDateKey(value);
                    }}
                    enabled={!!watch?.()?.frequency}
                    isMMYYYY={isMonthYearDatesEnabled()}
                    isYYYY={!isMonthYearDatesEnabled() && watch?.()?.frequency == 'Annually'}
                    options={[
                        {
                            label: (
                                <>
                                    <Typography variant="labels.largeDemibold" label={t('CLIENT_RET_DATE_LABEL')} />
                                    <Typography
                                        variant="labels.large"
                                        label={`${addAgeToDate(
                                            adviceClient?.clientDob,
                                            adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE,
                                            'MMMM YYYY'
                                        )}, ${t('AGE_LABEL')} ${adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE}`}
                                    />
                                </>
                            ),
                            value: addAgeToDate(
                                adviceClient?.clientDob,
                                adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE
                            ),
                            key: 'retirementAge',
                            disabled: !watch?.()?.frequency
                        },
                        adviceClient?.relationship?.[0]?.age && {
                            label: (
                                <>
                                    <Typography variant="labels.largeDemibold" label={t('COCLIENT_RET_DATE_LABEL')} />
                                    <Typography
                                        variant="labels.large"
                                        label={`${addAgeToDate(
                                            adviceClient?.relationship?.[0]?.age,
                                            adviceClient?.relationship?.[0]?.retirementAge || DEFAULT_RETIREMENT_AGE,
                                            'MMMM YYYY'
                                        )}, ${t('AGE_LABEL')} ${
                                            adviceClient?.relationship?.[0]?.retirementAge || DEFAULT_RETIREMENT_AGE
                                        }`}
                                    />
                                </>
                            ),
                            value: addAgeToDate(
                                adviceClient?.relationship?.[0]?.age,
                                adviceClient?.relationship?.[0]?.retirementAge || DEFAULT_RETIREMENT_AGE
                            ),
                            disabled: !watch?.()?.frequency
                        }
                    ]}
                />
            )
        },
        {
            name: 'targetDate',
            label: t('ENDING_AT_LABEL'),
            type: 'date',
            colspan: 1,
            required: true,
            validation: {
                validate: (value, formValues) => {
                    if (!value) {
                        return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    }
                    if (dayjs(value, 'DD-MM-YYYY').isBefore(dayjs(), isMonthYearDatesEnabled() ? 'month' : undefined)) {
                        return t('TEXT_VALID_DATE_ERROR');
                    }
                    if (
                        formValues &&
                        formValues.planStartRetirement &&
                        dayjs(value, 'DD-MM-YYYY').isBefore(dayjs(formValues.planStartRetirement, 'DD-MM-YYYY'))
                    ) {
                        return t('TEXT_VALID_END_YEAR_ERROR');
                    }
                    return true;
                }
            },
            customRender: ({ field, error, onChange, watch }) => {
                return (
                    <RadioWithOptionalDatePicker
                        showCustomDateField={false}
                        hasError={!!error && error.type !== 'required'}
                        field={{
                            ...field,
                            onChange: (arg: string) => {
                                field.onChange(arg);
                                onChange?.(arg);
                            }
                        }}
                        selectedKey={endDateKey || ''}
                        onSelectedKeyChange={setEndDateKey}
                        enabled={!!watch?.()?.frequency}
                        isMMYYYY={isMonthYearDatesEnabled()}
                        isYYYY={!isMonthYearDatesEnabled() && watch?.()?.frequency == 'Annually'}
                        options={[
                            {
                                label: (
                                    <>
                                        <Typography
                                            variant="labels.largeDemibold"
                                            label={t('CLIENT_PLAN_DATE_LABEL')}
                                        />
                                        <Typography
                                            variant="labels.large"
                                            label={`${addAgeToDate(
                                                adviceClient?.clientDob,
                                                adviceClient?.planningAge || DEFAULT_PLANNING_AGE,
                                                'MMMM YYYY'
                                            )}, ${t('AGE_LABEL')} ${adviceClient?.planningAge || DEFAULT_PLANNING_AGE}`}
                                        />
                                    </>
                                ),
                                value: addAgeToDate(
                                    adviceClient?.clientDob,
                                    adviceClient?.planningAge || DEFAULT_PLANNING_AGE
                                ),
                                key: 'planningAge',
                                disabled: !watch?.()?.frequency
                            },
                            adviceClient?.relationship?.[0]?.age && {
                                label: (
                                    <>
                                        <Typography
                                            variant="labels.largeDemibold"
                                            label={t('COCLIENT_PLAN_DATE_LABEL')}
                                        />
                                        <Typography
                                            variant="labels.large"
                                            label={`${addAgeToDate(
                                                adviceClient?.relationship?.[0]?.age,
                                                adviceClient?.relationship?.[0]?.planningAge || DEFAULT_PLANNING_AGE,
                                                'MMMM YYYY'
                                            )}, ${t('AGE_LABEL')} ${
                                                adviceClient?.relationship?.[0]?.planningAge || DEFAULT_PLANNING_AGE
                                            }`}
                                        />
                                    </>
                                ),
                                value: addAgeToDate(
                                    adviceClient?.relationship?.[0]?.age,
                                    adviceClient?.relationship?.[0]?.planningAge || DEFAULT_PLANNING_AGE
                                ),
                                disabled: !watch?.()?.frequency
                            }
                        ]}
                    />
                );
            }
        },
        {
            name: 'goalAmount',
            label: t('GOAL_SUMMARY_BEQUEST_AMOUNT_TEXT'),
            type: 'currency',
            required: false,
            customRender: ({ field, error, onChange }) => (
                <Box sx={{ width: '100%' }}>
                    <TextboxInput
                        onBlur={field.onBlur}
                        onChange={(e) => {
                            const numericValue = Number(removeNonNumeric(e.target.value));
                            onChange?.(isNaN(numericValue) ? 0 : numericValue);
                        }}
                        value={field.value ? formatCurrency(Number(field.value), true) : ''}
                        inputRef={field.ref}
                        size="small"
                        error={!!error}
                        type="text"
                        data-testid="retirement-goal-amount"
                        placeholder={currencySymbol || DEFAULT_CURRENCY_SYMBOL}
                        iconPlacement="start"
                        fullWidth
                    />
                </Box>
            ),
            infoTooltip: (
                <Trans
                    i18nKey="GOAL_SUMMARY_BEQUEST_AMOUNT_TEXT_INFO_TEXT"
                    components={{ strong: <strong />, br: <br /> }}
                />
            )
        }
    ];

    return (
        <ConfigurableDrawer<RetirementGoalsFormType>
            open={open}
            handleClose={handleClose}
            selectedGoal={selectedGoal}
            goalData={goalData}
            goalType={RETIREMENT_GOAL}
            fields={fields}
            defaultValues={initialValues}
            drawerLabel={t('BANORTE_RETIREMENT_GOAL_DRAWER_LABEL')}
            additionalFields={additionalFields}
        />
    );
};
